import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart,
  DollarSign,
  ShoppingCart,
  Truck,
  Factory,
  Monitor,
  Building2,
  Zap,
  Users,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  Globe,
  Shield,
  Brain,
  Cloud
} from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';

const IndustriesPage = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(0);
  const [hoveredCase, setHoveredCase] = useState(null);

  const industries = [
    {
      id: 'healthcare',
      icon: <Heart className="w-16 h-16" />,
      name: 'Healthcare & Life Sciences',
      tagline: 'Accelerating Innovation in Patient Care',
      description: 'Transforming healthcare delivery with AI-powered diagnostics, patient management systems, and compliance solutions that improve outcomes while reducing costs.',
      challenges: [
        'Complex regulatory compliance (HIPAA, FDA)',
        'Legacy system integration',
        'Patient data privacy and security',
        'Scalability for growing patient volumes',
        'Real-time clinical decision support'
      ],
      solutions: [
        'AI-powered diagnostic imaging analysis',
        'Electronic Health Record (EHR) optimization',
        'Telemedicine platform development',
        'Predictive analytics for patient outcomes',
        'Clinical trial management systems',
        'Medical IoT device integration'
      ],
      technologies: ['TensorFlow', 'FHIR', 'AWS HealthLake', 'React', 'Python', 'PostgreSQL'],
      metrics: [
        { label: 'Diagnostic Accuracy', value: '95%+', improvement: 'increase' },
        { label: 'Patient Processing Time', value: '40%', improvement: 'reduction' },
        { label: 'Compliance Violations', value: '90%', improvement: 'reduction' },
        { label: 'Treatment Costs', value: '25%', improvement: 'reduction' }
      ],
      caseStudies: [
        {
          title: 'AI-Powered Radiology Platform',
          description: 'Developed machine learning models for automated medical imaging analysis',
          result: 'Reduced diagnosis time by 60% while maintaining 98% accuracy'
        },
        {
          title: 'Patient Management System',
          description: 'Built comprehensive EHR system with predictive analytics',
          result: 'Improved patient outcomes by 35% through early intervention alerts'
        }
      ],
      color: 'from-red-400 to-pink-500',
      bgGradient: 'from-red-500/10 to-pink-600/10'
    },
    {
      id: 'fintech',
      icon: <DollarSign className="w-16 h-16" />,
      name: 'FinTech & Banking',
      tagline: 'Driving Innovation in Financial Services',
      description: 'Revolutionizing financial services with AI-driven risk assessment, fraud detection, algorithmic trading, and next-generation banking platforms.',
      challenges: [
        'Regulatory compliance (SOX, PCI DSS, KYC)',
        'Real-time transaction processing',
        'Fraud detection and prevention',
        'Legacy system modernization',
        'Customer data security and privacy'
      ],
      solutions: [
        'AI-powered credit scoring and risk assessment',
        'Real-time fraud detection systems',
        'Algorithmic trading platforms',
        'Digital banking and payment solutions',
        'Blockchain and cryptocurrency integration',
        'RegTech compliance automation'
      ],
      technologies: ['Machine Learning', 'Blockchain', 'Kafka', 'Redis', 'Java', 'React Native'],
      metrics: [
        { label: 'Fraud Detection Rate', value: '99.8%', improvement: 'accuracy' },
        { label: 'Transaction Processing', value: '70%', improvement: 'faster' },
        { label: 'Compliance Costs', value: '50%', improvement: 'reduction' },
        { label: 'Customer Onboarding', value: '80%', improvement: 'faster' }
      ],
      caseStudies: [
        {
          title: 'AI Risk Assessment Platform',
          description: 'Machine learning system for real-time credit scoring and loan approval',
          result: 'Reduced loan processing time by 70% while improving accuracy by 25%'
        },
        {
          title: 'Digital Banking Platform',
          description: 'Full-stack banking solution with mobile apps and web portal',
          result: 'Increased customer satisfaction by 45% and reduced operational costs by 35%'
        }
      ],
      color: 'from-green-400 to-emerald-500',
      bgGradient: 'from-green-500/10 to-emerald-600/10'
    },
    {
      id: 'ecommerce',
      icon: <ShoppingCart className="w-16 h-16" />,
      name: 'Retail & E-commerce',
      tagline: 'Optimizing Customer Experience',
      description: 'Enhancing retail operations with AI-powered recommendations, inventory optimization, personalized shopping experiences, and omnichannel solutions.',
      challenges: [
        'Inventory management and forecasting',
        'Personalized customer experiences',
        'Multi-channel integration',
        'Real-time analytics and reporting',
        'Supply chain optimization'
      ],
      solutions: [
        'AI-powered product recommendation engines',
        'Dynamic pricing optimization',
        'Inventory forecasting and management',
        'Customer behavior analytics',
        'Omnichannel commerce platforms',
        'Supply chain visibility solutions'
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'TensorFlow', 'AWS', 'Stripe API'],
      metrics: [
        { label: 'Conversion Rate', value: '35%', improvement: 'increase' },
        { label: 'Customer Retention', value: '50%', improvement: 'improvement' },
        { label: 'Inventory Costs', value: '30%', improvement: 'reduction' },
        { label: 'Page Load Time', value: '60%', improvement: 'faster' }
      ],
      caseStudies: [
        {
          title: 'E-commerce Platform with AI',
          description: 'Built modern e-commerce platform with personalized recommendations',
          result: 'Increased sales by 120% and improved customer satisfaction by 40%'
        },
        {
          title: 'Inventory Management System',
          description: 'AI-powered inventory forecasting and automatic reordering system',
          result: 'Reduced inventory costs by 25% while maintaining 99% product availability'
        }
      ],
      color: 'from-orange-400 to-red-500',
      bgGradient: 'from-orange-500/10 to-red-600/10'
    },
    {
      id: 'logistics',
      icon: <Truck className="w-16 h-16" />,
      name: 'Logistics & Supply Chain',
      tagline: 'Streamlining Operations',
      description: 'Optimizing supply chain operations with IoT integration, route optimization, predictive maintenance, and real-time tracking solutions.',
      challenges: [
        'Real-time shipment tracking',
        'Route optimization and fuel efficiency',
        'Warehouse automation',
        'Supply chain visibility',
        'Predictive maintenance for fleet'
      ],
      solutions: [
        'IoT-enabled fleet management systems',
        'AI-powered route optimization',
        'Warehouse management systems (WMS)',
        'Supply chain analytics platforms',
        'Predictive maintenance solutions',
        'Last-mile delivery optimization'
      ],
      technologies: ['IoT', 'Google Maps API', 'PostgreSQL', 'React Native', 'Python', 'Apache Kafka'],
      metrics: [
        { label: 'Delivery Time', value: '30%', improvement: 'faster' },
        { label: 'Fuel Costs', value: '25%', improvement: 'reduction' },
        { label: 'Vehicle Downtime', value: '40%', improvement: 'reduction' },
        { label: 'Customer Satisfaction', value: '50%', improvement: 'increase' }
      ],
      caseStudies: [
        {
          title: 'Fleet Management Platform',
          description: 'IoT-enabled tracking and route optimization for logistics company',
          result: 'Reduced fuel costs by 25% and improved delivery times by 30%'
        },
        {
          title: 'Warehouse Automation',
          description: 'AI-powered inventory management and robotic process automation',
          result: 'Increased warehouse efficiency by 60% and reduced errors by 80%'
        }
      ],
      color: 'from-blue-400 to-cyan-500',
      bgGradient: 'from-blue-500/10 to-cyan-600/10'
    },
    {
      id: 'manufacturing',
      icon: <Factory className="w-16 h-16" />,
      name: 'Manufacturing & IoT',
      tagline: 'Enabling Smart Manufacturing',
      description: 'Transforming manufacturing with Industry 4.0 solutions, predictive maintenance, quality control automation, and smart factory implementations.',
      challenges: [
        'Legacy equipment integration',
        'Predictive maintenance implementation',
        'Quality control automation',
        'Production optimization',
        'Supply chain coordination'
      ],
      solutions: [
        'Industrial IoT sensor networks',
        'Predictive maintenance platforms',
        'Quality control AI systems',
        'Production optimization algorithms',
        'Digital twin implementations',
        'Smart factory automation'
      ],
      technologies: ['IoT', 'Edge Computing', 'TensorFlow', 'MQTT', 'InfluxDB', 'Grafana'],
      metrics: [
        { label: 'Equipment Downtime', value: '45%', improvement: 'reduction' },
        { label: 'Quality Defects', value: '60%', improvement: 'reduction' },
        { label: 'Production Efficiency', value: '35%', improvement: 'increase' },
        { label: 'Maintenance Costs', value: '30%', improvement: 'reduction' }
      ],
      caseStudies: [
        {
          title: 'Smart Factory Implementation',
          description: 'IoT sensors and AI analytics for predictive maintenance',
          result: 'Reduced equipment downtime by 45% and maintenance costs by 30%'
        },
        {
          title: 'Quality Control Automation',
          description: 'Computer vision system for automated quality inspection',
          result: 'Improved quality detection by 60% and reduced inspection time by 70%'
        }
      ],
      color: 'from-purple-400 to-blue-500',
      bgGradient: 'from-purple-500/10 to-blue-600/10'
    },
    {
      id: 'saas',
      icon: <Monitor className="w-16 h-16" />,
      name: 'SaaS & Technology',
      tagline: 'Powering Digital Platforms',
      description: 'Building scalable SaaS platforms, API integrations, microservices architectures, and cloud-native solutions for technology companies.',
      challenges: [
        'Multi-tenant architecture design',
        'Scalability and performance',
        'API integration and management',
        'User authentication and authorization',
        'Data analytics and reporting'
      ],
      solutions: [
        'Multi-tenant SaaS platform development',
        'Microservices architecture implementation',
        'API gateway and management',
        'Cloud-native application development',
        'Analytics and business intelligence',
        'DevOps and CI/CD pipeline setup'
      ],
      technologies: ['React', 'Node.js', 'Kubernetes', 'AWS', 'MongoDB', 'GraphQL'],
      metrics: [
        { label: 'Platform Uptime', value: '99.9%', improvement: 'availability' },
        { label: 'API Response Time', value: '50%', improvement: 'faster' },
        { label: 'Development Speed', value: '70%', improvement: 'faster' },
        { label: 'Infrastructure Costs', value: '40%', improvement: 'reduction' }
      ],
      caseStudies: [
        {
          title: 'Multi-Tenant SaaS Platform',
          description: 'Scalable SaaS solution with advanced analytics and reporting',
          result: 'Supported 10,000+ users with 99.9% uptime and reduced infrastructure costs by 40%'
        },
        {
          title: 'API Management Platform',
          description: 'Comprehensive API gateway with monitoring and analytics',
          result: 'Improved API performance by 50% and reduced development time by 70%'
        }
      ],
      color: 'from-indigo-400 to-purple-500',
      bgGradient: 'from-indigo-500/10 to-purple-600/10'
    }
  ];

  const globalStats = [
    { icon: <Building2 className="w-8 h-8" />, value: '15+', label: 'Industries Served' },
    { icon: <Globe className="w-8 h-8" />, value: '500+', label: 'Projects Delivered' },
    { icon: <Users className="w-8 h-8" />, value: '150+', label: 'Enterprise Clients' },
    { icon: <TrendingUp className="w-8 h-8" />, value: '98%', label: 'Success Rate' }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEO
        title="Industries We Serve - Qivra Technologies | AI & IT Solutions Across Sectors"
        description="Qivra Technologies delivers intelligent AI and IT solutions across healthcare, fintech, e-commerce, manufacturing, and enterprise sectors. Intelligence unleashed for every industry."
        keywords="AI solutions by industry, healthcare technology, fintech AI, e-commerce solutions, manufacturing automation, enterprise AI, digital transformation by sector"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle, rgba(14, 165, 233, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 100% 100%, 60px 60px'
            }}
          />

          {/* Floating Logo Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Top Area */}
            <motion.div
              className="absolute top-24 left-12 opacity-6"
              animate={{
                y: [0, -24, 0],
                x: [0, 14, 0],
                rotate: [0, 2, 0]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-32 right-24 opacity-7"
              animate={{
                y: [0, -16, 0],
                x: [0, -10, 0],
                rotate: [0, -3, 0]
              }}
              transition={{
                duration: 11,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2.5
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>

            {/* Middle Area */}
            <motion.div
              className="absolute top-1/2 left-4 opacity-4"
              animate={{
                y: [0, -32, 0],
                x: [0, 24, 0],
                rotate: [0, 8, 0]
              }}
              transition={{
                duration: 17,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            {/* Bottom Area */}
            <motion.div
              className="absolute bottom-36 left-24 opacity-8"
              animate={{
                y: [0, -30, 0],
                x: [0, 22, 0],
                rotate: [0, 8, 0]
              }}
              transition={{
                duration: 13,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 5
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-24 right-16 opacity-9"
              animate={{
                y: [0, -28, 0],
                x: [0, -16, 0],
                rotate: [0, -5, 0]
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 7
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Cross-Industry Expertise</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
              <span className="text-white">Industries</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light">
                We Serve
              </span>
            </h1>

            <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
              Transforming businesses across sectors with intelligent technology solutions tailored for each industry
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industry Selector */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {industries.map((industry, idx) => (
              <motion.button
                key={industry.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedIndustry(idx)}
                className={`flex items-center gap-3 px-6 py-3 rounded-full font-semibold transition-all ${selectedIndustry === idx
                  ? 'bg-gradient-to-r from-cyan-400 to-purple-600 text-white shadow-lg shadow-cyan-400/50'
                  : 'bg-gray-800/50 text-gray-300 hover:text-cyan-400 border border-gray-700 hover:border-cyan-400/50'
                  }`}
              >
                <div className="w-6 h-6">{React.cloneElement(industry.icon, { className: "w-6 h-6" })}</div>
                <span className="hidden sm:inline">{industry.name}</span>
              </motion.button>
            ))}
          </div>

          {/* Selected Industry Details */}
          <motion.div
            key={selectedIndustry}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`bg-gradient-to-br ${industries[selectedIndustry].bgGradient} rounded-3xl border border-cyan-400/20 overflow-hidden`}
          >
            <div className="p-8 md:p-12">
              {/* Header Section */}
              <div className="flex items-start gap-6 mb-10">
                <div className="text-cyan-400 flex-shrink-0">
                  {industries[selectedIndustry].icon}
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl md:text-4xl font-light text-white mb-3">
                    {industries[selectedIndustry].name}
                  </h2>
                  <p className="text-xl text-cyan-400 mb-4 font-light">
                    {industries[selectedIndustry].tagline}
                  </p>
                  <p className="text-lg text-gray-400 leading-relaxed font-light max-w-3xl">
                    {industries[selectedIndustry].description}
                  </p>
                </div>
              </div>

              {/* Key Metrics - Full Width */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {industries[selectedIndustry].metrics.map((metric, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-black/30 p-5 rounded-2xl border border-gray-700 hover:border-cyan-400/50 transition-all text-center"
                  >
                    <div className="text-3xl font-light text-cyan-400 mb-2">
                      {metric.value}
                    </div>
                    <div className="text-sm text-gray-400 mb-1 font-light">{metric.label}</div>
                    <div className="text-xs text-gray-500 capitalize">{metric.improvement}</div>
                  </motion.div>
                ))}
              </div>

              {/* Compact Challenges & Solutions Grid */}
              <div className="grid lg:grid-cols-2 gap-8 mb-10">
                {/* Challenges - Compact */}
                <div>
                  <h3 className="text-xl font-light text-white mb-5 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    Key Challenges
                  </h3>
                  <div className="space-y-2">
                    {industries[selectedIndustry].challenges.slice(0, 3).map((challenge, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start gap-3 p-3 bg-black/20 rounded-xl hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/30"
                      >
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-400 text-sm font-light leading-relaxed">{challenge}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Solutions - Compact */}
                <div>
                  <h3 className="text-xl font-light text-white mb-5 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-green-400" />
                    Our Solutions
                  </h3>
                  <div className="space-y-2">
                    {industries[selectedIndustry].solutions.slice(0, 3).map((solution, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start gap-3 p-3 bg-black/20 rounded-xl hover:bg-green-500/10 transition-all border border-transparent hover:border-green-500/30"
                      >
                        <CheckCircle className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                        <span className="text-gray-400 text-sm font-light leading-relaxed">{solution}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Technologies - Bottom */}
              <div className="border-t border-gray-700/50 pt-8">
                <h3 className="text-lg font-light text-white mb-4">Technologies We Use</h3>
                <div className="flex flex-wrap gap-2">
                  {industries[selectedIndustry].technologies.map((tech, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="bg-gradient-to-r from-cyan-500/10 to-purple-600/10 text-cyan-300 px-4 py-2 rounded-full text-sm font-light border border-cyan-400/20 hover:border-cyan-400/50 transition-all"
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
              Success Stories in {industries[selectedIndustry].name}
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto font-light">
              Real-world examples of how we've transformed businesses in this industry
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {industries[selectedIndustry].caseStudies.map((caseStudy, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                onHoverStart={() => setHoveredCase(idx)}
                onHoverEnd={() => setHoveredCase(null)}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl border border-gray-700 hover:border-cyan-400/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${industries[selectedIndustry].color} flex items-center justify-center`}>
                    <span className="text-white font-bold text-lg">{idx + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                    {caseStudy.title}
                  </h3>
                </div>

                <p className="text-gray-400 mb-6 leading-relaxed">
                  {caseStudy.description}
                </p>

                <div className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 p-4 rounded-xl border border-cyan-400/30">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="text-white font-semibold">Result:</span>
                  </div>
                  <p className="text-cyan-400 font-medium">{caseStudy.result}</p>
                </div>

                <motion.div
                  initial={false}
                  animate={{ opacity: hoveredCase === idx ? 1 : 0 }}
                  className="mt-6 pt-6 border-t border-gray-700"
                >
                  <span className="text-cyan-400 font-semibold flex items-center gap-2">
                    View Full Case Study
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Expertise Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-light mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Cross-Industry Innovation
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Our deep industry knowledge combined with cutting-edge technology expertise
              enables us to deliver solutions that address your specific business challenges.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 p-8 rounded-3xl border border-cyan-400/30 text-center"
            >
              <Brain className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">AI-First Approach</h3>
              <p className="text-gray-400 leading-relaxed font-light">
                We integrate artificial intelligence into every solution, creating intelligent
                systems that learn, adapt, and optimize over time.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-600/10 to-pink-600/10 p-8 rounded-3xl border border-purple-400/30 text-center"
            >
              <Cloud className="w-16 h-16 text-purple-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Cloud-Native Solutions</h3>
              <p className="text-gray-400 leading-relaxed font-light">
                Built for scale and reliability, our cloud-native architectures ensure
                your solutions grow with your business needs.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-500/10 to-teal-600/10 p-8 rounded-3xl border border-green-400/30 text-center"
            >
              <Shield className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Security & Compliance</h3>
              <p className="text-gray-400 leading-relaxed font-light">
                Industry-specific security measures and compliance frameworks ensure
                your data and operations meet regulatory requirements.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready to Transform Your Industry?
            </h2>
            <p className="text-xl text-gray-400 mb-8 font-light">
              Let's discuss how our industry expertise and cutting-edge technology
              can drive innovation and growth in your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-400 to-purple-600 px-8 py-4 rounded-full font-bold text-white hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center gap-2 justify-center"
              >
                Get Industry Consultation
                <ArrowRight className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="/projects"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-full font-bold hover:bg-cyan-400 hover:text-black transition-all"
              >
                View Case Studies
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;